import { DungeonEntity } from './DungeonEntity';
import { DungeonEntityProps } from '../types/entity.types';
import { ActionDefinition } from '../types/types';
import { DirectionEnum, DungeonEntityType, Element } from '../types/enums';
import { getEquippedEmotionTypes } from '../services/SketchService';
import { challengeService } from '../services/ChallengeService';
import { dungeonService } from '../services/DungeonService';
import { netService } from '../services/NetService';
import { coopDeathClient } from '../services/CoopDeathClient';
import { coopDeathHost } from '../services/CoopDeathHost';
import { mapService } from '../services/MapService';
import { getTargetPosition } from '../utils/map.utils';
import { saveService } from '../services/SaveService';
import { spellExecutor } from '../services/SpellService';
import { SPELL_REGISTRY } from '../data/spells.config';
import { SpellDefinition } from '../types/spell.types';
import {
  EMPTY_PLAYER_SLOT,
  PERSO_SLOT_COUNT,
  PlayerSlotContent,
} from '../types/trinket.types';
import {
  TRINKET_MAJOR_REGISTRY,
  buildSpellFromTemplate,
} from '../data/trinkets.config';
import {
  liveLoadoutSource,
  type LoadoutSource,
} from '../services/LoadoutSourceService';

/** Fallback spell used when no major trinket is equipped (neutral fist). */
export const PLAYER_BASIC_ATTACK_ID = 'basic-strike';

export class PlayerEntity extends DungeonEntity {
  /**
   * Snapshot of equipped slots taken at run boot / restore from save — not
   * kept in sync with the store afterwards (except via syncSlotsFromLoadout).
   */
  public slots: PlayerSlotContent[] = [];

  public loadoutSource: LoadoutSource;

  godMode = false;
  noclip = false;
  /** Host-side: owner dropped mid-run; avatar stays in the sim (immobile,
   *  vulnerable) until they reconnect or it goes down. */
  disconnected = false;

  /** Host-side: true for a client-controlled avatar — drives the socket/mastery
   *  source (snapshot) vs the local player's live stores. */
  remote = false;

  // Host: FIFO of move intents received from the controlling client, applied one
  // per free tick (cf. processPendingMove). Queuing — rather than dropping a
  // move when the player is mid-step — makes the host follow the client's exact
  // path, so prediction stays in sync instead of diverging on a skipped step.
  private pendingMoves: { dir: DirectionEnum; seq: number }[] = [];

  constructor(
    props: Omit<DungeonEntityProps, 'type'> & { loadoutSource?: LoadoutSource },
  ) {
    super({ ...props, type: DungeonEntityType.PLAYER });
    this.loadoutSource = props.loadoutSource ?? liveLoadoutSource();
    this.slots = this.loadoutSource.slots.map((s) => ({
      spellId: s.spellId,
      minorId: s.minorId,
    }));
    if (this.slots.length !== PERSO_SLOT_COUNT) {
      this.slots = Array.from({ length: PERSO_SLOT_COUNT }, (_, i) => ({
        ...EMPTY_PLAYER_SLOT,
        ...this.slots[i],
      }));
    }
  }

  protected computeEmotionTypes(): Element[] {
    return getEquippedEmotionTypes(this.loadoutSource);
  }

  /**
   * Re-reads the live loadout store into `slots` — used when equipment changes
   * mid-run (e.g. equipping a spell unlocked by a grimoire), since `slots` is
   * otherwise a boot-time snapshot. Always reads the live source so local
   * behavior is unchanged; remote slot re-sync sets `slots` directly.
   */
  public syncSlotsFromLoadout(): void {
    const stored = liveLoadoutSource().slots;
    this.slots = Array.from({ length: PERSO_SLOT_COUNT }, (_, i) => ({
      ...EMPTY_PLAYER_SLOT,
      spellId: stored[i]?.spellId ?? null,
      minorId: stored[i]?.minorId ?? null,
    }));
  }

  /** cf. docs/game-design/equipment.md — basic attack resolution. */
  public getBasicAttackSpell(): SpellDefinition {
    const majorId = this.loadoutSource.major;
    const def =
      majorId !== null && this.loadoutSource.majorLevel !== null
        ? TRINKET_MAJOR_REGISTRY[majorId]
        : undefined;
    if (!def?.basicAttack) {
      return (
        this.getSpellInstance(PLAYER_BASIC_ATTACK_ID) ??
        SPELL_REGISTRY[PLAYER_BASIC_ATTACK_ID]
      );
    }
    return buildSpellFromTemplate(def.basicAttack, `trinket-attack:${def.id}`);
  }

  getSpellIds(): string[] {
    const slotSpells = this.slots
      .map((s) => s.spellId)
      .filter((id): id is string => !!id);
    return [this.getBasicAttackSpell().id, ...slotSpells];
  }

  getAvailableActions(): ActionDefinition[] {
    return [
      {
        name: 'move',
        parameter: { type: 'position' },
        execute: (x: number, y: number) => this.move(x, y),
      },
      {
        name: 'face',
        parameter: { type: 'direction' },
        execute: (direction: DirectionEnum) => this.face(direction),
      },
      ...this.getSpellIds()
        .map((id) => spellExecutor.toActionDefinition(this, id))
        .filter((a): a is ActionDefinition => a !== null),
    ];
  }

  basicAttack(targetX: number, targetY: number, castSeq?: number): boolean {
    return spellExecutor.execute(
      this,
      this.getBasicAttackSpell(),
      targetX,
      targetY,
      castSeq,
    );
  }

  face(direction: DirectionEnum): void {
    this.direction = direction;
    this.markDirty();
  }

  /** Host: enqueue a move intent from the controlling client. */
  public queueMove(dir: DirectionEnum, seq: number): void {
    this.pendingMoves.push({ dir, seq });
    // The client caps its in-flight moves; this is just a defensive ceiling
    // against a misbehaving/flooding peer so the queue can't grow unbounded.
    if (this.pendingMoves.length > 8) {
      this.pendingMoves.shift();
    }
  }

  /**
   * Host: apply the next queued move when the player is free, and ack it (only
   * an applied/resolved input advances `lastInputSeq`, so the client's
   * reconciliation and prediction cap see the true progress). A move blocked by
   * the map (wall/occupied) resolves as a turn and is still acked.
   */
  public processPendingMove(): void {
    if (this.disconnected || !this.canMove()) {
      return;
    }
    const next = this.pendingMoves.shift();
    if (!next) {
      return;
    }
    const target = getTargetPosition(this.positionX, this.positionY, next.dir);
    if (!this.move(target.x, target.y)) {
      this.face(next.dir);
    }
    this.lastInputSeq = next.seq;
  }

  public clearPendingMoves(): void {
    this.pendingMoves.length = 0;
  }

  protected moveBaseMs = 325;

  protected onMoveCommitted(targetX: number, targetY: number): void {
    // Revive trigger must run on BOTH host and client for the LOCAL player: the
    // seed lives in the client-local inventory, so a client revives by stepping
    // on the beacon itself (host applies it). Must be BEFORE the client return
    // below, which skips the host-only `onPositionChange` side effects.
    coopDeathClient.checkReviveAt(this, targetX, targetY);
    // Client predicts movement locally; host runs authoritative side effects.
    if (netService.isClient()) {
      if (this.id === dungeonService.localPlayerId) {
        dungeonService.inventoryService.pickupItemsInRange(
          this,
          this.stats.pickupReach,
        );
      }
      return;
    }
    this.onPositionChange(targetX, targetY);
    dungeonService.inventoryService.pickupItemsInRange(
      this,
      this.stats.pickupReach,
    );
  }

  death(): void {
    super.death();
    if (netService.isHost()) {
      coopDeathHost.handleHostPlayerDeath(this);
      return;
    }
    // Solo defeat path only: in a co-op session the client never runs death()
    // (combat is host-authoritative — the client sets life from snapshots and
    // the host drives death via the player-died flow, cf. CoopDeathHost).
    if (this.id !== dungeonService.localPlayerId) {
      return;
    }
    setTimeout(() => {
      dungeonService.emitGameEvent('defeat');
    }, 1000);
  }

  private onPositionChange(x: number, y: number): void {
    mapService.revealAround(x, y, this.stats.viewRange);
    dungeonService.locationService.checkTrapAtPosition(x, y);
    challengeService.checkEscape(mapService.getRoomIndexAt(x, y));
    this.awakenDormantBossInRoom(x, y);
    saveService.save();
  }

  private awakenDormantBossInRoom(x: number, y: number): void {
    const roomIndex = mapService.getRoomIndexAt(x, y);
    if (roomIndex === -1) {
      return;
    }
    for (const entity of dungeonService.getEntities()) {
      const m = entity as { aiEnabled?: boolean; dormantRoomIndex?: number };
      if (m.aiEnabled === false && m.dormantRoomIndex === roomIndex) {
        m.aiEnabled = true;
        m.dormantRoomIndex = -1;
      }
    }
  }
}
